import { useEffect } from "react";
import { useSnapshot } from "valtio";
import {
  organizationApprovedUrlsStore,
  resetOrganizationApprovedUrlsStore,
} from "../approvedUrlsStore";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { CreateApprovedUrlPayload } from "@services/organizations/commands/cmdCreateApprovedUrl";
import { UpdateApprovedUrlPayload } from "@services/organizations/commands/cmdUpdateApprovedUrl";
import showAlert from "src/shared/utils/generic.tsx";

const service = new OrganizationService();

const useOrganizationApprovedUrls = (organizationId: number) => {
  const snap = useSnapshot(organizationApprovedUrlsStore);

  useEffect(() => {
    if (!organizationId) return;
    if (organizationId !== organizationApprovedUrlsStore.organizationId) {
      resetOrganizationApprovedUrlsStore();
      organizationApprovedUrlsStore.organizationId = organizationId;
    }
    loadUrls(organizationId);
  }, [organizationId]);

  async function loadUrls(orgId: number) {
    organizationApprovedUrlsStore.isLoading = true;
    organizationApprovedUrlsStore.error = null;
    try {
      // "inherited" so the list shows everything the ad picker will offer, including
      // the parent's rows. Those are read-only here — they are edited on the parent.
      organizationApprovedUrlsStore.urls = await service.getApprovedUrls(orgId, "inherited", false);
    } catch {
      organizationApprovedUrlsStore.error = "Failed to load approved URLs.";
    } finally {
      organizationApprovedUrlsStore.isLoading = false;
    }
  }

  async function createUrl(payload: CreateApprovedUrlPayload) {
    organizationApprovedUrlsStore.isSaving = true;
    try {
      await service.createApprovedUrl(organizationId, payload);
      showAlert("Approved URL created successfully", "success", 3000);
      await loadUrls(organizationId);
    } catch (err) {
      // Surface the server's message (e.g. duplicate-URL 409) rather than a generic one.
      showAlert(err instanceof Error ? err.message : "Failed to create approved URL", "error", 4000);
      throw err;
    } finally {
      organizationApprovedUrlsStore.isSaving = false;
    }
  }

  async function updateUrl(approvedUrlId: number, payload: UpdateApprovedUrlPayload) {
    organizationApprovedUrlsStore.isSaving = true;
    try {
      await service.updateApprovedUrl(organizationId, approvedUrlId, payload);
      showAlert("Approved URL updated successfully", "success", 3000);
      await loadUrls(organizationId);
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to update approved URL", "error", 4000);
      throw err;
    } finally {
      organizationApprovedUrlsStore.isSaving = false;
    }
  }

  async function moveUrl(approvedUrlId: number, destinationOrganizationId: number) {
    organizationApprovedUrlsStore.isSaving = true;
    try {
      await service.updateApprovedUrl(organizationId, approvedUrlId, {
        organization_id: destinationOrganizationId,
      });
      showAlert("Approved URL moved successfully", "success", 3000);
      await loadUrls(organizationId);
    } catch (err) {
      // e.g. 409 when the destination already has that address, or 404 when the
      // caller cannot edit it.
      showAlert(err instanceof Error ? err.message : "Failed to move approved URL", "error", 4000);
      throw err;
    } finally {
      organizationApprovedUrlsStore.isSaving = false;
    }
  }

  async function deleteUrl(approvedUrlId: number) {
    organizationApprovedUrlsStore.isSaving = true;
    try {
      await service.deleteApprovedUrl(organizationId, approvedUrlId);
      showAlert("Approved URL deleted successfully", "success", 3000);
      await loadUrls(organizationId);
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to delete approved URL", "error", 4000);
      throw err;
    } finally {
      organizationApprovedUrlsStore.isSaving = false;
    }
  }

  return {
    urls: snap.urls,
    isLoading: snap.isLoading,
    isSaving: snap.isSaving,
    error: snap.error,
    createUrl,
    updateUrl,
    moveUrl,
    deleteUrl,
  };
};

export default useOrganizationApprovedUrls;
